import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { UkropTip } from '../tooltips/DiagramTooltipContext';

export type RibbonMenuItem = {
  id: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** If set, choosing the item opens a file picker with this accept list. */
  accept?: string;
};

export function RibbonDropMenu({
  label,
  icon,
  tip,
  disabled,
  items,
  onSelect,
  onPickFile,
}: {
  label: string;
  icon: ReactNode;
  tip?: string;
  disabled?: boolean;
  items: RibbonMenuItem[];
  onSelect?: (id: string) => void;
  onPickFile?: (id: string, file: File) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const menuId = useId();
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const place = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const width = 240;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    setPos({ top: r.bottom + 4, left });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const pick = (item: RibbonMenuItem) => {
    if (item.disabled) return;
    if (item.accept) {
      inputRefs.current[item.id]?.click();
      setOpen(false);
      return;
    }
    setOpen(false);
    onSelect?.(item.id);
  };

  return (
    <div className="ribbon-menu">
      {items.map((item) =>
        item.accept ? (
          <input
            key={item.id}
            ref={(el) => {
              inputRefs.current[item.id] = el;
            }}
            type="file"
            accept={item.accept}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPickFile?.(item.id, file);
              e.target.value = '';
            }}
          />
        ) : null,
      )}
      <UkropTip text={tip ?? label}>
        <button
          ref={btnRef}
          type="button"
          className={clsx('ribbon-btn', open && 'is-active', disabled && 'is-disabled')}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
        >
          <span className="ribbon-btn-icon">
            {icon}
            <ChevronDown size={10} strokeWidth={2.25} aria-hidden />
          </span>
          <span className="ribbon-btn-text">{label}</span>
        </button>
      </UkropTip>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={menuId}
              role="menu"
              className="ribbon-menu-panel"
              style={{ top: pos.top, left: pos.left }}
            >
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={clsx('ribbon-menu-item', item.disabled && 'is-disabled')}
                  disabled={item.disabled}
                  onClick={() => pick(item)}
                >
                  <span className="ribbon-menu-item-label">{item.label}</span>
                  {item.hint ? <span className="ribbon-menu-item-hint">{item.hint}</span> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
